import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { useAdminAuth } from "./AdminAuthContext";
import { useToast } from "./ToastProvider";
import { StatusPill, formatINR, fmtDate } from "./widgets";
import { X, Trash2, Upload, Search, Plus, GripVertical } from "lucide-react";

const empty = () => ({
  id: "",
  name: "",
  short_description: "",
  description: "",
  category: "staple",
  price_inr: 0,
  compare_at_price: null,
  sku: "",
  stock_qty: 0,
  sizes: ["S", "M", "L", "XL"],
  colors: [],
  images: [],
  videos: [],
  status: "draft",
  featured: false,
  tags: [],
  materials: "",
});

export default function AdminProducts() {
  const { apiFetch } = useAdminAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const reload = useCallback(() => {
    const url = `/admin/products?status_filter=${statusFilter}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
    apiFetch(url)
      .then(({ data }) => setList(data || []))
      .catch((e) => console.error("product list load failed:", e?.response?.data?.detail || e.message));
  }, [statusFilter, q, apiFetch]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (params.get("new") === "1") {
      setEditing(empty());
      setParams({});
    }
  }, [params, setParams]);

  // Escape closes the editor/delete dialog
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (confirmDel) setConfirmDel(null);
      else if (editing) setEditing(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, confirmDel]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id && list.some((p) => p.id === editing.id)) {
        await apiFetch(`/admin/products/${editing.id}`, {
          method: "PATCH",
          data: editing,
          headers: { "Content-Type": "application/json" },
        });
        toast("Product saved");
      } else {
        await apiFetch(`/admin/products`, {
          method: "POST",
          data: editing,
          headers: { "Content-Type": "application/json" },
        });
        toast("Product created");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast(e?.response?.data?.detail || "Save failed", "err");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      await apiFetch(`/admin/products/${confirmDel.id}`, { method: "DELETE" });
      toast("Product deleted");
      setConfirmDel(null);
      reload();
    } catch (e) {
      toast("Delete failed", "err");
    }
  };

  return (
    <>
      <div className="adm-page-head">
        <div>
          <h1 className="adm-h1">Products</h1>
          <div className="adm-h1-sub">{list.length} {list.length === 1 ? "product" : "products"}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div className="adm-search" style={{ width: 260 }}>
            <Search size={14} />
            <input className="adm-input" style={{ background: "transparent", border: 0, padding: 0, fontSize: 13 }} placeholder="Search name, sku..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className="adm-select" style={{ width: 140 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <button className="adm-btn" onClick={() => setEditing(empty())}><Plus size={14} /> New</button>
        </div>
      </div>

      <div className="adm-card" style={{ padding: 0 }}>
        <table className="adm-table">
          <thead>
            <tr>
              <th>Product</th><th>Category</th><th>SKU</th><th style={{ textAlign: "right" }}>Price</th><th style={{ textAlign: "right" }}>Stock</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? <tr><td colSpan={7} className="adm-empty">No products.</td></tr> :
              list.map((p) => (
                <tr key={p.id}>
                  <td className="clickable" onClick={() => setEditing(p)}>
                    <div className="adm-cell-name">
                      <img src={p.images?.[0]} alt="" className="adm-thumb" />
                      <div className="adm-cell-name-text">
                        <span>{p.name}</span>
                        <span className="adm-cell-name-sub">{p.id}</span>
                      </div>
                    </div>
                  </td>
                  <td className="adm-mono" style={{ textTransform: "uppercase", color: p.category === "exclusive" ? "#C42021" : "#B6B6BD" }}>{p.category}</td>
                  <td className="adm-mono" style={{ color: "#8B8B93" }}>{p.sku || "-"}</td>
                  <td className="adm-mono" style={{ textAlign: "right" }}>{formatINR(p.price_inr)}</td>
                  <td className="adm-mono" style={{ textAlign: "right", color: p.stock_qty === 0 ? "#EF4444" : p.stock_qty <= 5 ? "#F59E0B" : "#ECECEE" }}>{p.stock_qty}</td>
                  <td><StatusPill status={p.status} /></td>
                  <td><button className="adm-icon-btn" onClick={() => setConfirmDel(p)} aria-label="Delete"><Trash2 size={14} /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductDrawer
          product={editing}
          setProduct={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          saving={saving}
          apiFetch={apiFetch}
          toast={toast}
        />
      )}

      {confirmDel && (
        <div className="adm-modal-scrim" onClick={() => setConfirmDel(null)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Delete "{confirmDel.name}"?</h2>
            <p style={{ color: "#B6B6BD", fontSize: 13, marginTop: 8 }}>This removes the product and its images from the storefront. Orders containing it are unaffected.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
              <button className="adm-btn adm-btn-ghost" onClick={() => setConfirmDel(null)}>Keep</button>
              <button className="adm-btn adm-btn-danger" onClick={del}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

function ProductDrawer({ product, setProduct, onClose, onSave, saving, apiFetch, toast }) {
  const set = (k) => (e) => setProduct({ ...product, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setN = (k) => (e) => setProduct({ ...product, [k]: e.target.value === "" ? "" : Number(e.target.value) });
  const setArr = (k) => (e) => setProduct({ ...product, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) });

  const [uploading, setUploading] = useState(0);

  const onDrop = useCallback(async (files) => {
    for (const f of files) {
      if (f.size > MAX_IMAGE_BYTES) {
        toast(`${f.name} is over 8MB — compress it first`, "err");
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      setUploading((n) => n + 1);
      try {
        const res = await apiFetch(`/admin/products/upload-image`, {
          method: "POST",
          data: fd,
        });
        setProduct((p) => ({ ...p, images: [...(p.images || []), res.data.url] }));
      } catch (e) {
        console.error("image upload failed:", e?.response?.data?.detail || e.message);
        toast(e?.response?.data?.detail || `Upload failed for ${f.name}`, "err");
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }, [apiFetch, setProduct, toast]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "image/*": [] }, multiple: true,
  });

  const removeImg = (idx) => setProduct({ ...product, images: product.images.filter((_, i) => i !== idx) });
  const moveImg = (idx, dir) => {
    const next = [...product.images];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setProduct({ ...product, images: next });
  };

  return (
    <>
      <div className="adm-drawer-scrim" onClick={onClose} />
      <div className="adm-drawer">
        <div className="adm-drawer-h">
          <div>
            <div className="adm-card-title">{product.id ? "Edit product" : "New product"}</div>
            <div className="adm-card-sub adm-mono">{product.id || "Draft"}</div>
          </div>
          <button className="adm-icon-btn" onClick={onClose}><X size={15} /></button>
        </div>
        <div className="adm-drawer-b">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Name"><input className="adm-input" value={product.name} onChange={set("name")} /></Field>
            <Field label="SKU"><input className="adm-input" value={product.sku || ""} onChange={set("sku")} /></Field>
            <Field label="Category">
              <select className="adm-select" value={product.category} onChange={set("category")}>
                <option value="staple">Staple</option>
                <option value="exclusive">Exclusive</option>
              </select>
            </Field>
            <Field label="Status">
              <select className="adm-select" value={product.status} onChange={set("status")}>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field label="Price (INR)"><input className="adm-input adm-mono" type="number" value={product.price_inr} onChange={setN("price_inr")} /></Field>
            <Field label="Compare-at price"><input className="adm-input adm-mono" type="number" value={product.compare_at_price ?? ""} onChange={setN("compare_at_price")} /></Field>
            <Field label="Stock qty"><input className="adm-input adm-mono" type="number" value={product.stock_qty} onChange={setN("stock_qty")} /></Field>
            <Field label="Featured">
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#B6B6BD" }}>
                <input type="checkbox" checked={!!product.featured} onChange={set("featured")} /> Show on homepage
              </label>
            </Field>
            <Field label="Sizes (csv)" wide><input className="adm-input" value={(product.sizes || []).join(", ")} onChange={setArr("sizes")} /></Field>
            <Field label="Tags (csv)" wide><input className="adm-input" value={(product.tags || []).join(", ")} onChange={setArr("tags")} /></Field>
            <Field label="Short description" wide><input className="adm-input" value={product.short_description || ""} onChange={set("short_description")} /></Field>
            <Field label="Description" wide><textarea className="adm-textarea" value={product.description || ""} onChange={set("description")} /></Field>
            <Field label="Materials" wide><textarea className="adm-textarea" value={product.materials || ""} onChange={set("materials")} /></Field>
          </div>

          <div style={{ marginTop: 22 }}>
            <div className="adm-label">Images (first is the main)</div>
            <div {...getRootProps()} style={{ border: "1px dashed #2F2F36", borderRadius: 10, padding: 18, textAlign: "center", color: "#8B8B93", background: isDragActive ? "#1F1F23" : "transparent", cursor: "pointer", fontSize: 12.5 }}>
              <input {...getInputProps()} />
              <Upload size={16} style={{ marginBottom: 6 }} /><br />
              {isDragActive
                ? "Drop to upload"
                : uploading > 0
                ? `Uploading ${uploading} image${uploading === 1 ? "" : "s"}...`
                : "Drag & drop, or click to upload (JPG/PNG/WEBP, max 8MB)"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px,1fr))", gap: 10, marginTop: 12 }}>
              {(product.images || []).map((src, i) => (
                <div key={src + i} style={{ position: "relative", border: "1px solid #26262C", borderRadius: 8, overflow: "hidden", aspectRatio: "3/4" }}>
                  <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  {i === 0 && <span style={{ position: "absolute", top: 4, left: 4, background: "#8B5CF6", color: "#fff", fontSize: 10, padding: "2px 6px", borderRadius: 4, fontFamily: "JetBrains Mono" }}>MAIN</span>}
                  <div style={{ position: "absolute", bottom: 4, left: 4, right: 4, display: "flex", gap: 4, justifyContent: "space-between" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="adm-icon-btn" style={{ width: 24, height: 24 }} onClick={() => moveImg(i, -1)} disabled={i === 0}><GripVertical size={11} /></button>
                    </div>
                    <button className="adm-icon-btn" style={{ width: 24, height: 24 }} onClick={() => removeImg(i)}><Trash2 size={11} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="adm-drawer-f">
          <button className="adm-btn adm-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="adm-btn" onClick={onSave} disabled={saving || !product.name}>{saving ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </>
  );
}

const Field = ({ label, children, wide }) => (
  <div style={{ gridColumn: wide ? "1 / -1" : "auto" }}>
    <label className="adm-label">{label}</label>
    {children}
  </div>
);
